import { initFinder } from './finder.js';
import { initShop } from './shop.js';
import { initUgc } from './ugc.js';
import { attachCaptions } from './captions.js';

/* Pine Bros — landing page behavior.
   Motion + reveal rules per landing-page-design SKILL.md B7.
   No scroll listeners: IntersectionObserver only. */

const EASE_MS = 700;
const $ = (sel, root = document) => root.querySelector(sel);
const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ------------------------------------------- captions + third-party labels */
function initCaptions() {
  $$('video[data-captions]').forEach((v) => attachCaptions(v).load(v.dataset.captions));
}


// the announcement bar is fixed; publish its height so the spacer and the nav can sit below it
function initAnnounce() {
  const bar = document.querySelector('.announce');
  if (!bar) return;
  const set = () => document.documentElement.style.setProperty('--announce-h', bar.offsetHeight + 'px');
  set();
  window.addEventListener('resize', set);
  if (window.ResizeObserver) new ResizeObserver(set).observe(bar);
}

/* ---------------------------------------------------------------- nav */
function initNav() {
  const burger = $('[data-burger]');
  const menu = $('#menu');
  if (!burger || !menu) return;

  let lastFocused = null;

  const focusables = () =>
    $$('a[href], button:not([disabled])', menu).filter((el) => el.offsetParent !== null);

  function open() {
    lastFocused = document.activeElement;
    menu.hidden = false;
    // next frame so the transition runs from the closed state
    requestAnimationFrame(() => {
      menu.dataset.open = 'true';
      burger.setAttribute('aria-expanded', 'true');
      burger.setAttribute('aria-label', 'Close menu');
      document.body.style.overflow = 'hidden';
      const first = focusables()[0];
      if (first) first.focus();
    });
  }

  function close() {
    menu.dataset.open = 'false';
    burger.setAttribute('aria-expanded', 'false');
    burger.setAttribute('aria-label', 'Open menu');
    document.body.style.overflow = '';
    window.setTimeout(() => { menu.hidden = true; }, reduceMotion ? 0 : EASE_MS);
    // always hand focus back to the control that opened the dialog
    (lastFocused && lastFocused !== document.body ? lastFocused : burger).focus();
  }

  burger.addEventListener('click', () => {
    menu.dataset.open === 'true' ? close() : open();
  });

  // Escape closes; Tab cycles through the burger and the menu links, so the control that closes the menu stays reachable
  document.addEventListener('keydown', (e) => {
    if (menu.dataset.open !== 'true') return;
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    if (e.key !== 'Tab') return;
    const items = [burger, ...focusables()];
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    else if (!items.includes(document.activeElement)) { e.preventDefault(); first.focus(); }
  });

  $$('a', menu).forEach((a) => a.addEventListener('click', close));
}

/* --------------------------------------------------- current section */
function initCurrentSection() {
  const links = $$('.nav__link');
  const map = new Map();
  links.forEach((l) => {
    const id = (l.getAttribute('href') || '').split('#')[1] || '';
    const section = id ? document.getElementById(id) : null;
    if (section) map.set(section, l);
  });
  if (!map.size) return;

  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      links.forEach((l) => l.removeAttribute('aria-current'));
      const link = map.get(entry.target);
      if (link) link.setAttribute('aria-current', 'true');
    });
  }, { rootMargin: '-45% 0px -50% 0px' });

  map.forEach((_, section) => io.observe(section));
}

/* ----------------------------------------------- B7 scroll reveal */
function initReveal() {
  const items = $$('.reveal');
  if (!items.length) return;
  if (reduceMotion) { items.forEach((el) => (el.dataset.shown = 'true')); return; }

  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.dataset.shown = 'true';
      io.unobserve(entry.target);
    });
  }, { rootMargin: '0px 0px -12% 0px', threshold: 0.1 });

  items.forEach((el) => io.observe(el));
}

/* ------------------------------- B11 tagline, word by word reveal */
function initTagline() {
  const el = $('[data-tagline]');
  if (!el) return;

  const words = el.textContent.trim().split(/\s+/);
  el.textContent = '';
  words.forEach((w, i) => {
    const span = document.createElement('span');
    span.className = 'tagline__w';
    span.dataset.on = 'false';
    span.textContent = w;
    el.append(span);
    if (i < words.length - 1) el.append(document.createTextNode(' '));
  });

  const spans = $$('.tagline__w', el);
  if (reduceMotion) { spans.forEach((s) => (s.dataset.on = 'true')); return; }

  // each word crosses its own trigger line independently
  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.dataset.on = 'true';
      io.unobserve(entry.target);
    });
  }, { rootMargin: '0px 0px -42% 0px', threshold: 0.6 });

  spans.forEach((s) => io.observe(s));

  /* A fast scroll can jump the whole trigger band, stranding a line of words in
     the muted tone permanently. Once the block has fully passed above the fold,
     light up anything the per word observer missed. */
  const sweep = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting || entry.boundingClientRect.bottom > 0) return;
      spans.forEach((s) => { s.dataset.on = 'true'; });
      sweep.disconnect();
    });
  }, { threshold: 0 });
  sweep.observe(el);
}

/* ------------------------------------------------------------ FAQ */
function initFaq() {
  $$('[data-faq] .faq__q').forEach((btn) => {
    const panel = document.getElementById(btn.getAttribute('aria-controls'));
    if (!panel) return;
    panel.inert = btn.getAttribute('aria-expanded') !== 'true';

    btn.addEventListener('click', () => {
      const isOpen = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', String(!isOpen));
      panel.inert = isOpen;

      if (isOpen) {
        panel.style.height = `${panel.scrollHeight}px`;
        requestAnimationFrame(() => { panel.style.height = '0px'; });
      } else {
        panel.style.height = `${panel.scrollHeight}px`;
        const done = () => {
          panel.style.height = 'auto';
          panel.removeEventListener('transitionend', done);
        };
        panel.addEventListener('transitionend', done);
      }
    });
  });
}

/* Email signup: the footer form is a real Shopify customer form and submits on its own. */

/* ------------------------------------------------ Enter submits a search */
/* Browsers submit a form on Enter by themselves, but only through a trusted default action that
   embedded browsers, some extensions and test drivers do not perform. Doing it explicitly costs
   nothing and means Enter in either store search box always searches. */
function initEnterSubmits() {
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' || e.isComposing) return;
    const input = e.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'search' || !input.form) return;
    e.preventDefault();
    input.form.requestSubmit();
  });
}

/* --------------------------------------------- Phosphor icon sprite */
/* ------------------------------------------------------------ go */
/* The icon sprite is inlined by layout/theme.liquid (snippets/pb-icons.liquid), so nothing waits on a request. */

// each module runs in isolation so one failure cannot blank the page
for (const init of [initNav, initEnterSubmits, initCurrentSection, initTagline, initFaq, initCaptions, initAnnounce, initFinder, initShop, initUgc, initReveal]) {
  try { init(); } catch (err) { console.error(`[pine-bros] ${init.name} failed`, err); }
}
// signup forms: Shopify marks every customer form on the page as posted; keep only the one that was sent
(() => {
  const q = new URLSearchParams(window.location.search);
  document.querySelectorAll('[data-success-for]').forEach((el) => {
    const key = el.dataset.successFor;
    if (!q.has(key)) { const form = el.closest('form'); if (form && form.id === 'restock') { el.remove(); } else { el.remove(); } }
  });
})();
